import React, { useState, useEffect } from "react";
import { useAuth } from "../../Context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Modal } from "react-bootstrap";
import { FaShieldAlt, FaClock, FaHeadset } from "react-icons/fa";
import "./LandingPage.css";
import { isExperimental } from "../../utils/isExperimental";

const LandingPage = () => {
  const { user, loginWithSSO, switchRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLoginFailedModal, setShowLoginFailedModal] = useState(false);

  const handleLoginClick = () => {
    setShowLoginModal(true);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const loginError = params.get("loginError");

    if (loginError === "unauthorized-email") {
      setShowLoginFailedModal(true);

      const nextParams = new URLSearchParams(location.search);
      nextParams.delete("loginError");

      navigate(
        {
          pathname: location.pathname,
          search: nextParams.toString() ? `?${nextParams.toString()}` : "",
        },
        { replace: true }
      );
    }
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (showLoginModal) {
      /* global google */
      if (window.google) {
        window.google.accounts.id.renderButton(
          document.getElementById("googleLoginDiv"),
          {
            theme: "outline",
            size: "large",
            width: "100%",
          }
        );

        window.google.accounts.id.prompt();
      }
    }
  }, [showLoginModal]);

  useEffect(() => {
    if (user) {
      setShowLoginModal(false);
      navigate("/complaints-website");
    }
  }, [user, navigate]);

  return (
    <div className="landing-page">
      <nav className="landing-navbar">
        <h1 className="nav-logo">THRIVE</h1>
        <div className="college-name">VNRVJIET</div>
      </nav>

      <main className="hero-section">
        <div className="hero-content">
          {user ? (
            <div className="user-section">
              <p className="hero-label">
                VNRVJIET REPORTING & RESOLUTION PORTAL
              </p>

              <h1 className="hero-title">
                Welcome back,
                <span>{user.name || user.email}</span>
              </h1>

              <p className="hero-description">
                Help us maintain campus standards by reporting issues or
                tracking your existing requests.
              </p>

              <div className="action-btns">
                <Button
                  className="action-btn view-complaints-btn"
                  onClick={() => navigate("/all-complaints")}
                >
                  View Requests
                </Button>

                <Button
                  className="action-btn submit-complaint-btn"
                  onClick={() => navigate("/complaint-form")}
                >
                  Report New Request
                </Button>
              </div>
            </div>
          ) : (
            <div className="guest-section">
              {isExperimental ? (
                <>
                  <p className="hero-label">THRIVE DEMO</p>

                  <h1 className="hero-title">
                    Experimental
                    <span>Demo Mode.</span>
                  </h1>

                  <p className="hero-description">
                    Explore the platform by selecting a role below. No login
                    required.
                  </p>

                  <div className="demo-buttons">
                    <Button
                      variant="light"
                      onClick={() => switchRole("student")}
                    >
                      🎓 Student
                    </Button>

                    <Button
                      variant="light"
                      onClick={() => switchRole("admin")}
                    >
                      👨‍💼 Admin
                    </Button>

                    <Button
                      variant="light"
                      onClick={() => switchRole("superadmin")}
                    >
                      ⚡ Super Admin
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="hero-label">
                    VNRVJIET REPORTING & RESOLUTION PORTAL
                  </p>

                  <h1 className="hero-title">
                    Campus concerns
                    <span>deserve to be heard.</span>
                  </h1>

                  <p className="hero-description">
                    A transparent platform for VNRVJIET students to securely
                    report campus concerns and track their resolution.
                  </p>

                  <Button
                    onClick={handleLoginClick}
                    className="google-login-btn"
                  >
                    <div className="btn-content">
                      <svg
                        width="23"
                        height="23"
                        viewBox="0 0 24 24"
                        className="google-icon"
                      >
                        <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />

                        <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />

                        <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />

                        <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>

                      <span>Sign in with Google</span>
                    </div>
                  </Button>

                  <p className="login-note">
                    Use only your official{" "}
                    <strong>@vnrvjiet.in</strong> college email address
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </main>

      {!user && !isExperimental && (
        <section className="feature-strip">
          <div className="feature-item">
            <div className="feature-icon-box">
              <FaShieldAlt />
            </div>

            <div>
              <h3>Report Securely</h3>
              <p>Confidential request submission</p>
            </div>
          </div>

          <div className="feature-item">
            <div className="feature-icon-box">
              <FaClock />
            </div>

            <div>
              <h3>Track Status</h3>
              <p>Real-time request tracking</p>
            </div>
          </div>

          <div className="feature-item">
            <div className="feature-icon-box">
              <FaHeadset />
            </div>

            <div>
              <h3>Get Resolution</h3>
              <p>Admin support for quick resolution</p>
            </div>
          </div>
        </section>
      )}

      <Modal
        show={showLoginFailedModal}
        onHide={() => setShowLoginFailedModal(false)}
        centered
        dialogClassName="custom-login-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Login Failed</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="modal-welcome-text">
            Please sign in using your authorized organization email address.
            Personal email accounts are not allowed.
          </p>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowLoginFailedModal(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showLoginModal}
        onHide={() => setShowLoginModal(false)}
        centered
        dialogClassName="custom-login-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Sign in with Google</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="google-icon-circle">
            <svg width="30" height="30" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />

              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />

              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />

              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 0 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
          </div>

          <p className="modal-welcome-text">
            Sign in securely using your official{" "}
            <strong>@vnrvjiet.in</strong> email.
          </p>

          <div id="googleLoginDiv"></div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default LandingPage;
